import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Bot, Loader2, LogIn, LogOut, Play, Plus, Search, Trash2, UploadCloud, Wrench } from "lucide-react";
import type { PortType, WorkflowEdge, WorkflowNode, WorkflowNodeType } from "./types";
import { canConnect, hasSameEdge, uid } from "./utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type ConnectFrom = { fromNodeId: string; fromPortId: string; fromType: PortType };
type HoverTarget = { nodeId: string; portId: string; type: PortType } | null;

const agentOptions = [
  { id: "planner", name: "自主规划Agent" },
  { id: "multimodal", name: "多模态分析Agent" },
  { id: "rl", name: "强化学习Agent" },
] as const;

const toolOptions = [
  { id: "web_scholar", name: "Web_Scholar_Search" },
  { id: "openreview", name: "OpenReview" },
  { id: "materials_project", name: "Materials_Project_API" },
  { id: "arxiv", name: "ArXiv_Search" },
  { id: "dft_calculator", name: "DFT_Calculator" },
  { id: "literature_extraction", name: "Literature_Extraction_Tool" },
  { id: "element_frequency_stats", name: "Metal_Element_Frequency_Stats" },
  { id: "hea_recommendation", name: "HEA_Recommendation_Tool" },
  { id: "hea_structure_generation", name: "HEA_Structure_Generation_Tool" },
  { id: "mace_activity_eval", name: "MACE_HER_Activity_Evaluator" },
  { id: "hea_gradient_optimization", name: "HEA_Gradient_Optimization_Tool" },
] as const;

const nodeCatalog: Array<{
  type: WorkflowNodeType;
  title: string;
  subtitle?: string;
  inputs: Array<{ label: string; type: PortType }>;
  outputs: Array<{ label: string; type: PortType }>;
}> = [
  {
    type: "systemInput",
    title: "开始",
    subtitle: "系统输入",
    inputs: [],
    outputs: [{ label: "input", type: "text" }],
  },
  {
    type: "agent",
    title: "Agent",
    subtitle: "推理与规划",
    inputs: [{ label: "input", type: "text" }],
    outputs: [{ label: "output", type: "text" }],
  },
  {
    type: "tool",
    title: "工具调用",
    subtitle: "MCP/Skill",
    inputs: [{ label: "params", type: "json" }],
    outputs: [{ label: "result", type: "json" }],
  },
  {
    type: "systemOutput",
    title: "结束",
    subtitle: "系统输出",
    inputs: [{ label: "output", type: "text" }],
    outputs: [],
  },
];

function typeBadge(t: WorkflowNodeType) {
  if (t === "systemInput") return { bg: "bg-primary/10", fg: "text-primary", text: "输入" };
  if (t === "systemOutput") return { bg: "bg-amber-500/10", fg: "text-amber-600", text: "输出" };
  if (t === "tool") return { bg: "bg-emerald-500/10", fg: "text-emerald-700", text: "工具" };
  return { bg: "bg-slate-500/10", fg: "text-slate-700", text: "Agent" };
}

function portColor(type: PortType) {
  if (type === "text") return "#3D69E8";
  if (type === "json") return "#10B981";
  if (type === "image") return "#F18710";
  if (type === "file") return "#8B5CF6";
  return "#64748B";
}

function catalogIcon(t: WorkflowNodeType) {
  if (t === "systemInput") return LogIn;
  if (t === "systemOutput") return LogOut;
  if (t === "tool") return Wrench;
  return Bot;
}

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

export default function WorkflowCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement>(null);

  const [name, setName] = useState("HEA_workflow");
  const [saved, setSaved] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(true);
  const [paletteQuery, setPaletteQuery] = useState("");

  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });

  const [nodes, setNodes] = useState<WorkflowNode[]>(() => [
    {
      id: "n_start",
      x: 120,
      y: 220,
      data: {
        title: "开始",
        subtitle: "系统输入",
        nodeType: "systemInput",
        inputs: [],
        outputs: [{ id: "p_out", kind: "output", label: "input", type: "text" }],
        config: {
          inputKey: "input",
          userInput:
            "设计适用于碱性析氢反应的高活性高熵合金催化剂，其中元素候选池设置为7，初始推荐合金数量设置为3。",
        },
      },
    },
    {
      id: "n_end",
      x: 820,
      y: 260,
      data: {
        title: "结束",
        subtitle: "系统输出",
        nodeType: "systemOutput",
        inputs: [{ id: "p_in", kind: "input", label: "output", type: "text" }],
        outputs: [],
        config: { outputKey: "output" },
      },
    },
  ]);

  const [edges, setEdges] = useState<WorkflowEdge[]>([]);
  const [connectFrom, setConnectFrom] = useState<ConnectFrom | null>(null);
  const [hoverTarget, setHoverTarget] = useState<HoverTarget>(null);
  const [connectingTo, setConnectingTo] = useState<{ x: number; y: number } | null>(null); // world coords
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [runningNodeIds, setRunningNodeIds] = useState<string[]>([]);
  const [ranNodeIds, setRanNodeIds] = useState<string[]>([]);
  const [runningEdgeIds, setRunningEdgeIds] = useState<string[]>([]);
  const [ranEdgeIds, setRanEdgeIds] = useState<string[]>([]);
  const runTimersRef = useRef<number[]>([]);
  const [quickAdd, setQuickAdd] = useState<{
    open: boolean;
    clientX: number;
    clientY: number;
    worldX: number;
    worldY: number;
    from: ConnectFrom;
  } | null>(null);

  const [drag, setDrag] = useState<{
    kind: "node" | "pan";
    nodeId?: string;
    startX: number;
    startY: number;
    startPanX: number;
    startPanY: number;
    startNodeX?: number;
    startNodeY?: number;
  } | null>(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [reportHtml, setReportHtml] = useState<string>("");
  const [reportLoading, setReportLoading] = useState(false);

  const buildReportHtml = (prompt: string) => {
    const title = "碱性析氢反应高活性高熵合金催化剂设计";
    const sub = "基于文献挖掘与参与优化的高性能催化剂开发";
    const safe = (s: string) =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\"/g, "&quot;");
    const p = safe(prompt);
    const steps = [
      { name: "从领域文献中提取 HEA 组分数据", tag: "进行中", desc: "从文献与数据库中抽取候选元素与配比统计，形成初始候选池。", items: ["多源抓取（DOI/arXiv/数据库）", "合金成分识别与归一化", "元素候选池（≤7）构建"] },
      { name: "生成初始候选合金", tag: "就绪", desc: "根据候选池与频率/约束生成初始推荐（3 个），并输出结构/成分描述。", items: ["频率先验 + 约束采样", "初始推荐数（3）", "结构候选生成"] },
      { name: "活性评估与优化", tag: "就绪", desc: "对候选合金进行 HER 活性评估，并迭代优化到更优区间。", items: ["代理模型/DFT 评估（可选）", "梯度/贝叶斯优化", "输出最优建议"] },
    ];

    return `<!doctype html>
<html lang="zh-CN">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${title}</title>
  <style>
    :root { --bg:#0b1220; --card:#0f172a; --muted:#94a3b8; --text:#e5e7eb; --primary:#0ea5e9; --ring:rgba(14,165,233,.35); }
    *{box-sizing:border-box} body{margin:0;font-family:ui-sans-serif,system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,"Apple Color Emoji","Segoe UI Emoji";background:var(--bg);color:var(--text)}
    .wrap{max-width:980px;margin:0 auto;padding:28px}
    .hero{border-radius:18px;background:linear-gradient(135deg,#0ea5e9 0%,#2563eb 55%,#60a5fa 100%);padding:22px 22px 18px;box-shadow:0 18px 60px rgba(2,6,23,.35)}
    .hero h1{margin:0;font-size:28px;letter-spacing:.2px}
    .hero p{margin:8px 0 0;color:rgba(255,255,255,.9);line-height:1.6}
    .pill{display:inline-flex;align-items:center;gap:8px;margin-top:12px;background:rgba(255,255,255,.18);border:1px solid rgba(255,255,255,.25);color:#fff;border-radius:999px;padding:8px 12px;font-size:12px;font-weight:600}
    .grid{display:grid;grid-template-columns:1.2fr .8fr;gap:16px;margin-top:16px}
    .card{background:rgba(15,23,42,.92);border:1px solid rgba(148,163,184,.14);border-radius:16px;padding:16px;box-shadow:0 10px 36px rgba(2,6,23,.25)}
    .card h2{margin:0 0 10px;font-size:14px}
    .muted{color:var(--muted);font-size:13px;line-height:1.6}
    .steps{display:flex;flex-direction:column;gap:12px}
    .step{border-radius:14px;border:1px solid rgba(148,163,184,.14);background:rgba(2,6,23,.25);padding:14px}
    .row{display:flex;align-items:center;justify-content:space-between;gap:10px}
    .tag{font-size:12px;font-weight:700;border-radius:999px;padding:6px 10px;background:rgba(14,165,233,.16);border:1px solid rgba(14,165,233,.25);color:#c7f3ff}
    ul{margin:10px 0 0;padding-left:18px;color:rgba(229,231,235,.92)}
    li{margin:6px 0;font-size:13px}
    code{background:rgba(2,6,23,.45);padding:2px 6px;border-radius:8px;border:1px solid rgba(148,163,184,.14)}
    .qa{margin-top:12px;display:flex;flex-wrap:wrap;gap:8px}
    .q{font-size:12px;color:rgba(226,232,240,.9);border:1px solid rgba(148,163,184,.14);background:rgba(2,6,23,.18);padding:10px 12px;border-radius:12px}
    @media (max-width: 980px){ .grid{grid-template-columns:1fr} }
  </style>
</head>
<body>
  <div class="wrap">
    <div class="hero">
      <h1>${title}</h1>
      <p>${sub}</p>
      <div class="pill">当前目标：<span style="font-weight:800">${"碱性 HER 催化剂"}</span></div>
    </div>

    <div class="grid">
      <div class="card">
        <h2>步骤流</h2>
        <div class="steps">
          ${steps
            .map(
              (s, i) => `
            <div class="step">
              <div class="row">
                <div style="font-weight:800">步骤 ${i + 1}：${s.name}</div>
                <div class="tag">${s.tag}</div>
              </div>
              <div class="muted" style="margin-top:8px">${s.desc}</div>
              <ul>
                ${s.items.map((it) => `<li>${it}</li>`).join("")}
              </ul>
            </div>`,
            )
            .join("")}
        </div>
      </div>

      <div class="card">
        <h2>输入（Prompt）</h2>
        <div class="muted"><code>${p}</code></div>

        <div style="height:14px"></div>
        <h2>参数</h2>
        <div class="muted">元素候选池：<b>7</b></div>
        <div class="muted">初始推荐合金数量：<b>3</b></div>

        <div style="height:14px"></div>
        <h2>你可以继续问</h2>
        <div class="qa">
          <div class="q">候选池元素怎么从文献中自动提取？</div>
          <div class="q">如何把活性评估接入 DFT/代理模型？</div>
          <div class="q">如何把推荐结果导出为 CSV？</div>
        </div>
      </div>
    </div>
  </div>
</body>
</html>`;
  };

  const handleRun = () => {
    if (running) return;

    // clear any previous timers
    runTimersRef.current.forEach((t) => window.clearTimeout(t));
    runTimersRef.current = [];

    setReportOpen(false);
    setReportLoading(false);
    setRunning(true);
    setRanNodeIds([]);
    setRanEdgeIds([]);
    setRunningEdgeIds([]);
    setRunningNodeIds([]);
    setSelectedEdgeId(null);

    const start = nodes.find((n) => n.id === "n_start") ?? nodes.find((n) => n.data.nodeType === "systemInput");
    const startId = start?.id ?? "";

    // Build deterministic BFS layers from start following directed edges (parallel nodes in same layer highlight together)
    const outMap = new Map<string, string[]>();
    const edgeIdByPair = new Map<string, string>();
    for (const e of edges) {
      const list = outMap.get(e.fromNodeId) ?? [];
      list.push(e.toNodeId);
      outMap.set(e.fromNodeId, list);
      edgeIdByPair.set(`${e.fromNodeId}__${e.toNodeId}`, e.id);
    }
    for (const [k, v] of outMap) outMap.set(k, [...new Set(v)].sort());

    const dist = new Map<string, number>();
    if (startId) {
      const q: string[] = [startId];
      dist.set(startId, 0);
      while (q.length) {
        const cur = q.shift()!;
        const d = dist.get(cur) ?? 0;
        for (const nxt of outMap.get(cur) ?? []) {
          if (!dist.has(nxt)) {
            dist.set(nxt, d + 1);
            q.push(nxt);
          }
        }
      }
    }

    const maxD = Math.max(0, ...Array.from(dist.values()));
    const layers: string[][] = [];
    for (let d = 0; d <= maxD; d++) {
      const layer = Array.from(dist.entries())
        .filter(([, dd]) => dd === d)
        .map(([id]) => id)
        .sort();
      if (layer.length) layers.push(layer);
    }

    const edgesByLayer = (d: number) => {
      // edges that go from layer d-1 -> layer d
      if (d <= 0) return [] as string[];
      const res: string[] = [];
      for (const e of edges) {
        const df = dist.get(e.fromNodeId);
        const dt = dist.get(e.toNodeId);
        if (df === d - 1 && dt === d) res.push(e.id);
      }
      return res.sort();
    };

    const stepMs = 650;
    layers.forEach((layer, idx) => {
      runTimersRef.current.push(
        window.setTimeout(() => {
          setRunningNodeIds(layer);
          setRanNodeIds((prev) => {
            const set = new Set(prev);
            layer.forEach((id) => set.add(id));
            return Array.from(set);
          });

          const eids = edgesByLayer(idx);
          setRunningEdgeIds(eids);
          setRanEdgeIds((prev) => {
            const set = new Set(prev);
            eids.forEach((id) => set.add(id));
            return Array.from(set);
          });
        }, idx * stepMs),
      );
    });

    runTimersRef.current.push(
      window.setTimeout(() => {
        const prompt = String((start?.data.config?.userInput as string) ?? "").trim() || "（空）";
        setRunningNodeIds([]);
        setRunningEdgeIds([]);
        setRunning(false);
        // show skeleton first, then render report after 1s
        const html = buildReportHtml(prompt);
        setReportLoading(true);
        setReportOpen(true);
        runTimersRef.current.push(
          window.setTimeout(() => {
            setReportHtml(html);
            setReportLoading(false);
          }, 1000),
        );
      }, Math.max(1, layers.length) * stepMs + 250),
    );
  };

  const DEFAULT_START_PROMPT =
    "设计适用于碱性析氢反应的高活性高熵合金催化剂，其中元素候选池设置为7，初始推荐合金数量设置为3。";

  useEffect(() => {
    return () => {
      runTimersRef.current.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  const catalogFiltered = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    if (!q) return nodeCatalog;
    return nodeCatalog.filter((n) => `${n.title} ${n.subtitle ?? ""}`.toLowerCase().includes(q));
  }, [paletteQuery]);

  const toWorld = (clientX: number, clientY: number) => {
    const el = containerRef.current;
    if (!el) return { x: 0, y: 0 };
    const rect = el.getBoundingClientRect();
    const x = (clientX - rect.left - pan.x) / zoom;
    const y = (clientY - rect.top - pan.y) / zoom;
    return { x, y };
  };

  const addNode = (type: WorkflowNodeType, at?: { x: number; y: number }) => {
    const base = nodeCatalog.find((n) => n.type === type)!;
    const id = uid("node");
    const world = at
      ? at
      : (() => {
          const center = containerRef.current
            ? { x: containerRef.current.clientWidth / 2, y: containerRef.current.clientHeight / 2 }
            : { x: 520, y: 320 };
          return { x: (center.x - pan.x) / zoom, y: (center.y - pan.y) / zoom };
        })();

    setNodes((prev) => [
      ...prev,
      {
        id,
        x: world.x - 160,
        y: world.y - 40,
        data: {
          title: base.title,
          subtitle: base.subtitle,
          nodeType: base.type,
          inputs: base.inputs.map((p) => ({ id: uid("in"), kind: "input", label: p.label, type: p.type })),
          outputs: base.outputs.map((p) => ({ id: uid("out"), kind: "output", label: p.label, type: p.type })),
          config:
            base.type === "agent"
              ? { agentId: agentOptions[0]?.id ?? "" }
              : base.type === "tool"
                ? { toolId: toolOptions[0]?.id ?? "" }
                : {},
        },
      },
    ]);
    return id;
  };

  const getNode = (id: string) => nodes.find((n) => n.id === id)!;

  const onStartConnect = (nodeId: string, portId: string, type: PortType, e: React.MouseEvent) => {
    e.stopPropagation();
    setQuickAdd(null);
    setHoverTarget(null);
    setDrag(null);
    setConnectFrom({ fromNodeId: nodeId, fromPortId: portId, fromType: type });
    setConnectingTo(toWorld(e.clientX, e.clientY));
  };

  const onFinishConnect = (nodeId: string, portId: string, type: PortType) => {
    if (!connectFrom) return false;
    if (connectFrom.fromNodeId === nodeId) {
      setConnectFrom(null);
      setConnectingTo(null);
      return true;
    }
    if (!canConnect(connectFrom.fromType, type)) {
      setConnectFrom(null);
      setConnectingTo(null);
      return true;
    }

    const candidate = {
      fromNodeId: connectFrom.fromNodeId,
      fromPortId: connectFrom.fromPortId,
      toNodeId: nodeId,
      toPortId: portId,
    };
    setEdges((prev) => (hasSameEdge(prev, candidate) ? prev : [...prev, { id: uid("edge"), ...candidate }]));
    setConnectFrom(null);
    setConnectingTo(null);
    return true;
  };

  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.12;
    const newZoom = clamp(zoom * delta, 0.4, 2.2);
    const el = containerRef.current;
    if (!el) return setZoom(newZoom);
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setPan((p) => ({
      x: mx - (mx - p.x) * (newZoom / zoom),
      y: my - (my - p.y) * (newZoom / zoom),
    }));
    setZoom(+newZoom.toFixed(3));
  };

  const onMouseDownCanvas = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest("[data-node]")) return;
    if (connectFrom) return;
    setSelectedEdgeId(null);
    setDrag({
      kind: "pan",
      startX: e.clientX,
      startY: e.clientY,
      startPanX: pan.x,
      startPanY: pan.y,
    });
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (connectFrom) {
      setConnectingTo(toWorld(e.clientX, e.clientY));
      return;
    }
    if (!drag) return;
    if (drag.kind === "pan") {
      setPan({ x: drag.startPanX + (e.clientX - drag.startX), y: drag.startPanY + (e.clientY - drag.startY) });
      return;
    }
    if (drag.kind === "node" && drag.nodeId) {
      const dx = (e.clientX - drag.startX) / zoom;
      const dy = (e.clientY - drag.startY) / zoom;
      setNodes((prev) =>
        prev.map((n) =>
          n.id === drag.nodeId
            ? { ...n, x: (drag.startNodeX ?? n.x) + dx, y: (drag.startNodeY ?? n.y) + dy }
            : n,
        ),
      );
    }
  };

  const onMouseUp = (e?: React.MouseEvent) => {
    if (connectFrom && e) {
      // If released on an input port, that handler will close it. Otherwise open quick add.
      if (hoverTarget) {
        const consumed = onFinishConnect(hoverTarget.nodeId, hoverTarget.portId, hoverTarget.type);
        if (consumed) return;
      }
      const world = toWorld(e.clientX, e.clientY);
      setQuickAdd({
        open: true,
        clientX: e.clientX,
        clientY: e.clientY,
        worldX: world.x,
        worldY: world.y,
        from: connectFrom,
      });
      setConnectFrom(null);
      setConnectingTo(null);
      setHoverTarget(null);
      return;
    }
    setDrag(null);
  };

  const portAnchor = (node: WorkflowNode, portId: string) => {
    const width = 240;
    const header = 44;
    const rowH = 24;
    const inIndex = node.data.inputs.findIndex((p) => p.id === portId);
    if (inIndex >= 0) {
      return { x: node.x, y: node.y + header + inIndex * rowH + 13 };
    }
    const outIndex = node.data.outputs.findIndex((p) => p.id === portId);
    if (outIndex >= 0) {
      return { x: node.x + width, y: node.y + header + outIndex * rowH + 13 };
    }
    return { x: node.x, y: node.y };
  };

  const edgePath = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const dx = Math.max(60, Math.abs(b.x - a.x) * 0.45);
    const c1 = { x: a.x + dx, y: a.y };
    const c2 = { x: b.x - dx, y: b.y };
    return `M ${a.x} ${a.y} C ${c1.x} ${c1.y}, ${c2.x} ${c2.y}, ${b.x} ${b.y}`;
  };

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 1400);
  };

  const loadWorkflow = (workflowId: string) => {
    if (workflowId !== "wf_hea") return;

    setName("S1-MatAgent_HEA_closed_loop_HER");

    // Phase 1 (Subtask 1): literature mining -> element frequency
    // Phase 2 (Subtask 2): HEA recommendation -> MLIP(MACE) evaluation -> gradient optimization -> experimental validation

    // Layout: make it shorter (less horizontal), more parallel (more vertical lanes)
    const start: WorkflowNode = {
      id: "n_start",
      x: 120,
      y: 260,
      data: {
        title: "开始",
        subtitle: "任务输入",
        nodeType: "systemInput",
        inputs: [],
        outputs: [{ id: "p_out", kind: "output", label: "input", type: "text" }],
        config: {
          inputKey: "input",
          userInput:
            DEFAULT_START_PROMPT,
        },
      },
    };

    // Step 0: planning -> multi-source retrieval (parallel)
    const agentPlan: WorkflowNode = {
      id: "n_agent_plan",
      x: 520,
      y: 260,
      data: {
        title: "任务规划",
        subtitle: "拆解 + 生成检索策略",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "text" }],
        outputs: [{ id: "p_out", kind: "output", label: "queries", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "把用户目标拆解为：检索关键词/限定条件/候选池与数量/评价指标。输出 JSON：{keywords:[...], sources:['arxiv','web_scholar','openreview'], years:'last 5y', target:'alkaline HER', elementPool:7, initAlloys:3}.",
        },
      },
    };

    const toolWebScholar: WorkflowNode = {
      id: "n_tool_webscholar",
      x: 920,
      y: 80,
      data: {
        title: "Web Scholar 检索",
        subtitle: "多源学术检索",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "papers", type: "json" }],
        config: {
          toolId: "web_scholar",
          paramsHint: "{ keywords:[...], years:'last 5y', topK: 200 }",
        },
      },
    };

    const toolArxiv: WorkflowNode = {
      id: "n_tool_arxiv",
      x: 920,
      y: 260,
      data: {
        title: "ArXiv 检索",
        subtitle: "预印本补全",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "papers", type: "json" }],
        config: {
          toolId: "arxiv",
          paramsHint: "{ query:'high entropy alloy alkaline HER', maxResults: 100 }",
        },
      },
    };

    const toolOpenReview: WorkflowNode = {
      id: "n_tool_openreview",
      x: 920,
      y: 440,
      data: {
        title: "OpenReview 检索",
        subtitle: "方法与模型参考",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "papers", type: "json" }],
        config: {
          toolId: "openreview",
          paramsHint: "{ query:'materials discovery HEA catalyst', topK: 50 }",
        },
      },
    };

    const toolMaterialsProjectLit: WorkflowNode = {
      id: "n_tool_matproj_lit",
      x: 920,
      y: 620,
      data: {
        title: "Materials Project 检索",
        subtitle: "相关相/稳定性线索",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "hints", type: "json" }],
        config: {
          toolId: "materials_project",
          paramsHint: "{ query:'HEA catalyst stability', topK: 50 }",
        },
      },
    };

    const toolWebScholarPatents: WorkflowNode = {
      id: "n_tool_patents",
      x: 920,
      y: 800,
      data: {
        title: "专利/工程文献检索",
        subtitle: "补充工艺与配方",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "papers", type: "json" }],
        config: {
          toolId: "web_scholar",
          paramsHint: "{ keywords:[...], sources:['patents','industry'], topK: 80 }",
        },
      },
    };

    const agentMergePapers: WorkflowNode = {
      id: "n_agent_merge",
      x: 1320,
      y: 260,
      data: {
        title: "文献汇总与去重",
        subtitle: "合并多源结果",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "papers", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "合并来自多个来源的 papers 列表，按 DOI/标题去重，保留：title, doi, year, abstract, url。输出 JSON：{papers:[...], total, deduped}.",
        },
      },
    };

    const toolExtract: WorkflowNode = {
      id: "n_tool_extract",
      x: 1720,
      y: 120,
      data: {
        title: "文献配方抽取",
        subtitle: "正则/元素名归一",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "formulas", type: "json" }],
        config: {
          toolId: "literature_extraction",
          paramsHint: "{ papers: [...], task: 'extract chemical formulas' }",
        },
      },
    };

    const toolFreq: WorkflowNode = {
      id: "n_tool_freq",
      x: 1720,
      y: 320,
      data: {
        title: "金属元素频次统计",
        subtitle: "自动写代码+画直方图",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "stats", type: "json" }],
        config: {
          toolId: "element_frequency_stats",
          paramsHint: "{ formulas: [...], topK: 10 }",
          expectedTop10: "Ni, Pt, Co, Fe, Mo, Ru, Cu, Ir, Pd, Rh",
        },
      },
    };

    const agentSpace: WorkflowNode = {
      id: "n_agent_space",
      x: 2120,
      y: 220,
      data: {
        title: "搜索空间收敛",
        subtitle: "从 20M → nickel-based 子空间",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "space", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "根据元素频次统计结果，构造 5 元 HEA 搜索空间：以 Ni 为基底 + 4 个高频元素组合（Co/Fe/Mo/Ru/Cu/Ir/Pd/Rh/Pt 等）。输出 JSON：{base:'Ni', candidates:[...], combinations_estimate:20000000, narrowed_strategy:'nickel-based + four high-probability metals'}。",
        },
      },
    };

    const toolRecommend: WorkflowNode = {
      id: "n_tool_reco",
      x: 2520,
      y: 120,
      data: {
        title: "HEA 组成推荐",
        subtitle: "基于文献综合分析（ScienceOne）",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "recommendations", type: "json" }],
        config: {
          toolId: "hea_recommendation",
          paramsHint: "{ space: {...}, target: 'alkaline HER', outputCount: 50 }",
        },
      },
    };

    const toolMaterialsDb: WorkflowNode = {
      id: "n_tool_matdb",
      x: 2520,
      y: 320,
      data: {
        title: "材料数据库补全",
        subtitle: "MP/相图/可获得元素",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "properties", type: "json" }],
        config: {
          toolId: "materials_project",
          paramsHint: "{ elements:[...], query:'phase stability, cost, availability' }",
        },
      },
    };

    const agentMergeReco: WorkflowNode = {
      id: "n_agent_merge_reco",
      x: 2920,
      y: 220,
      data: {
        title: "候选汇合",
        subtitle: "融合推荐 + 数据库约束",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "candidates", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "融合推荐列表与材料数据库属性约束（相稳定/价格/可获得性），输出清洗前候选 JSON：{candidates:[...], notes:[...]}。",
        },
      },
    };

    const agentSynthFeasibility: WorkflowNode = {
      id: "n_agent_synth",
      x: 3320,
      y: 120,
      data: {
        title: "可合成性/成本筛选",
        subtitle: "工程约束过滤",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "filtered", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "对推荐候选做工程约束过滤：贵金属比例、合成可行性、元素毒性/供应风险。输出 JSON：{candidates:[...], dropped:[...]}。",
        },
      },
    };

    const agentClean: WorkflowNode = {
      id: "n_agent_clean",
      x: 3320,
      y: 320,
      data: {
        title: "合法性过滤与格式化",
        subtitle: "去重/修正/约束检查",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "candidates", type: "json" }],
        config: {
          agentId: "planner",
          prompt:
            "对推荐组成做：去重、元素合法性检查（限定金属集合）、配比格式统一（如 Ni4Co4Cu1Mo3Ru4）、过滤不合理组合。输出 JSON：{candidates:[{composition, references, rationale}]}。",
        },
      },
    };

    const toolStruct: WorkflowNode = {
      id: "n_tool_struct",
      x: 3720,
      y: 80,
      data: {
        title: "结构生成",
        subtitle: "HEA slab + 中间体",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "structures", type: "json" }],
        config: {
          toolId: "hea_structure_generation",
          paramsHint: "{ candidates:[...], surfaces:['(111)'], intermediates:['H2O*','H*','OH*'], sites:'all' }",
        },
      },
    };

    const toolMace: WorkflowNode = {
      id: "n_tool_mace",
      x: 3720,
      y: 260,
      data: {
        title: "HER 活性评估（MLIP）",
        subtitle: "fine-tuned MACE (RMSE ~3.3 meV/atom)",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "metrics", type: "json" }],
        config: {
          toolId: "mace_activity_eval",
          paramsHint:
            "{ structures:[...], mechanism:'Volmer-Tafel', compute:['water_adsorption','water_dissociation_barrier','hydrogen_adsorption_free_energy'], siteAggregation:'average_over_sites' }",
          descriptorNote:
            "Paper: in alkaline, use inverse of water dissociation barrier as activity descriptor during optimization; final screening also considers water adsorption & Tafel step enthalpy changes.",
        },
      },
    };

    const toolOptimize: WorkflowNode = {
      id: "n_tool_opt",
      x: 4120,
      y: 180,
      data: {
        title: "梯度优化（闭环）",
        subtitle: "基于 MACE 计算图梯度",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "optimized", type: "json" }],
        config: {
          toolId: "hea_gradient_optimization",
          paramsHint:
            "{ candidates:[...], metrics:[...], optimize:'maximize 1/barrier', actions:['ratio_update','element_replace'], stop:'no significant increase' }",
          note:
            "Iteratively adjust element ratios and sequentially replace element types according to gradient magnitude w.r.t activity.",
        },
      },
    };

    const toolReport: WorkflowNode = {
      id: "n_tool_report",
      x: 4520,
      y: 80,
      data: {
        title: "可视化报告生成",
        subtitle: "结构化摘要 + 图表",
        nodeType: "tool",
        inputs: [{ id: "p_in", kind: "input", label: "params", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "report", type: "text" }],
        config: {
          toolId: "literature_extraction",
          paramsHint: "{ results:{...}, format:'html', sections:['pipeline','topk','checklist'] }",
        },
      },
    };

    const agentSelect: WorkflowNode = {
      id: "n_agent_select",
      x: 4520,
      y: 260,
      data: {
        title: "专家筛选与实验方案",
        subtitle: "Top-5 for synthesis",
        nodeType: "agent",
        inputs: [{ id: "p_in", kind: "input", label: "input", type: "json" }],
        outputs: [{ id: "p_out", kind: "output", label: "output", type: "text" }],
        config: {
          agentId: "planner",
          prompt:
            "根据优化后的候选与评估指标，筛选适合实验合成的 Top-5，并给出实验验证方案：\n- 目标指标：η10、η100、η500、Tafel slope、EIS、Cdl\n- 稳定性：500 h @ 500 mA cm−2\n- 表征：SEM/元素映射一致性\n输出推荐列表（示例：Ni4Mo4Ru4Pd1Pt3、Ni5Fe3Mo4Ru1Pd3、Ni4Mo4Ru3Rh1Pt4、Ni4Fe4Mo4Pd1Pt3、Ni4Co4Cu1Mo3Ru4）与验证 checklist。",
        },
      },
    };

    const end: WorkflowNode = {
      id: "n_end",
      x: 4920,
      y: 200,
      data: {
        title: "结束",
        subtitle: "系统输出",
        nodeType: "systemOutput",
        inputs: [{ id: "p_in", kind: "input", label: "output", type: "text" }],
        outputs: [],
        config: { outputKey: "output" },
      },
    };

    const newNodes = [
      start,
      agentPlan,
      toolWebScholar,
      toolArxiv,
      toolOpenReview,
      toolMaterialsProjectLit,
      toolWebScholarPatents,
      agentMergePapers,
      toolExtract,
      toolFreq,
      agentSpace,
      toolRecommend,
      toolMaterialsDb,
      agentMergeReco,
      agentSynthFeasibility,
      agentClean,
      toolStruct,
      toolMace,
      toolOptimize,
      toolReport,
      agentSelect,
      end,
    ];

    const newEdges: WorkflowEdge[] = [
      { id: uid("edge"), fromNodeId: start.id, fromPortId: "p_out", toNodeId: agentPlan.id, toPortId: "p_in" },

      // parallel retrieval
      { id: uid("edge"), fromNodeId: agentPlan.id, fromPortId: "p_out", toNodeId: toolWebScholar.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentPlan.id, fromPortId: "p_out", toNodeId: toolArxiv.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentPlan.id, fromPortId: "p_out", toNodeId: toolOpenReview.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentPlan.id, fromPortId: "p_out", toNodeId: toolMaterialsProjectLit.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentPlan.id, fromPortId: "p_out", toNodeId: toolWebScholarPatents.id, toPortId: "p_in" },

      // merge then extract
      { id: uid("edge"), fromNodeId: toolWebScholar.id, fromPortId: "p_out", toNodeId: agentMergePapers.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolArxiv.id, fromPortId: "p_out", toNodeId: agentMergePapers.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolOpenReview.id, fromPortId: "p_out", toNodeId: agentMergePapers.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolMaterialsProjectLit.id, fromPortId: "p_out", toNodeId: agentMergePapers.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolWebScholarPatents.id, fromPortId: "p_out", toNodeId: agentMergePapers.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentMergePapers.id, fromPortId: "p_out", toNodeId: toolExtract.id, toPortId: "p_in" },

      // main chain
      { id: uid("edge"), fromNodeId: toolExtract.id, fromPortId: "p_out", toNodeId: toolFreq.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolFreq.id, fromPortId: "p_out", toNodeId: agentSpace.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentSpace.id, fromPortId: "p_out", toNodeId: toolRecommend.id, toPortId: "p_in" },

      // parallel enrichment: materials DB constraints
      { id: uid("edge"), fromNodeId: agentSpace.id, fromPortId: "p_out", toNodeId: toolMaterialsDb.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolRecommend.id, fromPortId: "p_out", toNodeId: agentMergeReco.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolMaterialsDb.id, fromPortId: "p_out", toNodeId: agentMergeReco.id, toPortId: "p_in" },

      // parallel: feasibility filtering
      { id: uid("edge"), fromNodeId: agentMergeReco.id, fromPortId: "p_out", toNodeId: agentSynthFeasibility.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentMergeReco.id, fromPortId: "p_out", toNodeId: agentClean.id, toPortId: "p_in" },

      // downstream evaluation + optimization
      { id: uid("edge"), fromNodeId: agentSynthFeasibility.id, fromPortId: "p_out", toNodeId: agentClean.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentClean.id, fromPortId: "p_out", toNodeId: toolStruct.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolStruct.id, fromPortId: "p_out", toNodeId: toolMace.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: toolMace.id, fromPortId: "p_out", toNodeId: toolOptimize.id, toPortId: "p_in" },

      // parallel: report
      { id: uid("edge"), fromNodeId: toolOptimize.id, fromPortId: "p_out", toNodeId: toolReport.id, toPortId: "p_in" },

      // final
      { id: uid("edge"), fromNodeId: toolOptimize.id, fromPortId: "p_out", toNodeId: agentSelect.id, toPortId: "p_in" },
      { id: uid("edge"), fromNodeId: agentSelect.id, fromPortId: "p_out", toNodeId: end.id, toPortId: "p_in" },
    ];

    setNodes(newNodes);
    setEdges(newEdges);
    setSelectedEdgeId(null);
    setQuickAdd(null);
    setPaletteOpen(false);
    setConnectFrom(null);
    setConnectingTo(null);
    setHoverTarget(null);
    setZoom(0.42);
    setPan({ x: 70, y: 60 });
  };

  useEffect(() => {
    const workflowId = searchParams.get("workflowId");
    if (workflowId) loadWorkflow(workflowId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      <div className="relative flex h-14 items-center border-b border-border bg-white px-4">
        <div className="flex min-w-0 items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            className="rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            返回
          </button>
          <div className="h-5 w-px bg-border" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-9 w-[240px] rounded-lg border border-input bg-background px-3 text-sm font-semibold outline-none focus:ring-2 focus:ring-ring"
          />
          {saved && <span className="text-xs text-success">已保存</span>}
        </div>

        <div className="ml-auto flex items-center justify-end gap-2">
          <button
            onClick={() => setPaletteOpen((v) => !v)}
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-input bg-background px-3 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-accent"
          >
            <Plus className="h-4 w-4" />
            节点库
          </button>
          <button
            onClick={handleSave}
            className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:opacity-90"
          >
            保存
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(148,163,184,0.25) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div
          ref={containerRef}
          className="relative min-h-0 flex-1 overflow-hidden"
          onWheel={onWheel}
          onMouseDown={onMouseDownCanvas}
          onMouseMove={onMouseMove}
          onMouseUp={(e) => onMouseUp(e)}
          onMouseLeave={() => {
            setDrag(null);
            setHoverTarget(null);
            setConnectFrom(null);
            setConnectingTo(null);
          }}
          onClick={() => {
            setConnectFrom(null);
            setConnectingTo(null);
            setQuickAdd(null);
            setHoverTarget(null);
            setSelectedEdgeId(null);
          }}
          style={{ cursor: connectFrom ? "crosshair" : drag?.kind === "pan" ? "grabbing" : "grab", userSelect: "none" }}
        >
          <div
            className="absolute inset-0"
            style={{
              transform: `translate(${Math.round(pan.x)}px, ${Math.round(pan.y)}px)`,
              transformOrigin: "0 0",
              willChange: "transform",
            }}
          >
            <div
              className="absolute inset-0"
              style={{
                zoom,
                transformOrigin: "0 0",
              }}
            >
              <svg className="absolute inset-0 h-full w-full" style={{ overflow: "visible" }}>
                {edges.map((e) => {
                  const from = getNode(e.fromNodeId);
                  const to = getNode(e.toNodeId);
                  const a = portAnchor(from, e.fromPortId);
                  const b = portAnchor(to, e.toPortId);
                  const isSel = selectedEdgeId === e.id;
                  const isRan = running && ranEdgeIds.includes(e.id);
                  const isActive = running && runningEdgeIds.includes(e.id);
                  const stroke = isSel
                    ? "rgba(61,105,232,0.95)"
                    : isActive
                      ? "rgba(61,105,232,0.92)"
                      : isRan
                        ? "rgba(61,105,232,0.62)"
                        : "rgba(148,163,184,0.38)";
                  const strokeWidth = isSel ? 2.8 : isActive ? 2.6 : isRan ? 2.3 : 2.1;
                  return (
                    <g key={e.id}>
                      <path
                        d={edgePath(a, b)}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={strokeWidth}
                        style={{ transition: "stroke 180ms ease, stroke-width 180ms ease" }}
                      />
                      <path
                        d={edgePath(a, b)}
                        fill="none"
                        stroke="transparent"
                        strokeWidth={10}
                        style={{ cursor: "pointer" }}
                        onClick={(evt) => {
                          evt.stopPropagation();
                          setSelectedEdgeId(e.id);
                        }}
                      />
                    </g>
                  );
                })}
                {connectFrom && connectingTo && (() => {
                  const from = getNode(connectFrom.fromNodeId);
                  const a = portAnchor(from, connectFrom.fromPortId);
                  const b = connectingTo;
                  return (
                    <path
                      d={edgePath(a, b)}
                      fill="none"
                      stroke="rgba(100,116,139,0.35)"
                      strokeWidth={2}
                      strokeDasharray="6 8"
                    />
                  );
                })()}
              </svg>

              {nodes.map((n) => {
                const badge = typeBadge(n.data.nodeType);
                const isRunningNode = running && runningNodeIds.includes(n.id);
                const isRan = running && ranNodeIds.includes(n.id);
                return (
                  <div
                    key={n.id}
                    data-node
                    className={[
                      "absolute w-[240px] overflow-hidden rounded-2xl border bg-white shadow-sm transition-[box-shadow,border-color] duration-200",
                      isRunningNode
                        ? "border-primary shadow-[0_18px_60px_rgba(61,105,232,0.25)]"
                        : isRan
                          ? "border-primary/60 shadow-[0_14px_42px_rgba(61,105,232,0.18)]"
                          : "border-border",
                    ].join(" ")}
                    style={{ left: n.x, top: n.y, cursor: drag?.kind === "node" && drag.nodeId === n.id ? "grabbing" : "grab" }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                      setSelectedEdgeId(null);
                      setDrag({
                        kind: "node",
                        nodeId: n.id,
                        startX: e.clientX,
                        startY: e.clientY,
                        startPanX: pan.x,
                        startPanY: pan.y,
                        startNodeX: n.x,
                        startNodeY: n.y,
                      });
                    }}
                  >
                  <div className="flex h-11 items-center justify-between border-b border-border px-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-semibold">{n.data.title}</span>
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${badge.bg} ${badge.fg}`}>{badge.text}</span>
                      </div>
                      {n.data.subtitle && <div className="truncate text-[11px] text-muted-foreground">{n.data.subtitle}</div>}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setNodes((prev) => prev.filter((x) => x.id !== n.id));
                        setEdges((prev) => prev.filter((x) => x.fromNodeId !== n.id && x.toNodeId !== n.id));
                      }}
                      className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white shadow-sm transition-colors hover:bg-red-700"
                    >
                      删除
                    </button>
                  </div>

                  <div className="px-3 py-2">
                    {(n.data.nodeType === "agent" || n.data.nodeType === "tool") && (
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{n.data.nodeType === "agent" ? "智能体" : "工具"}</span>
                        <Select
                          value={
                            n.data.nodeType === "agent"
                              ? String((n.data.config?.agentId as string) ?? agentOptions[0]?.id ?? "")
                              : String((n.data.config?.toolId as string) ?? toolOptions[0]?.id ?? "")
                          }
                          onValueChange={(v) => {
                            setNodes((prev) =>
                              prev.map((x) => {
                                if (x.id !== n.id) return x;
                                if (x.data.nodeType === "agent") return { ...x, data: { ...x.data, config: { ...(x.data.config ?? {}), agentId: v } } };
                                if (x.data.nodeType === "tool") return { ...x, data: { ...x.data, config: { ...(x.data.config ?? {}), toolId: v } } };
                                return x;
                              }),
                            );
                          }}
                        >
                          <SelectTrigger className="h-9 flex-1 min-w-0 overflow-hidden pr-8">
                            {(() => {
                              const placeholder = n.data.nodeType === "agent" ? "选择智能体" : "选择工具";
                              const opts = n.data.nodeType === "agent" ? agentOptions : toolOptions;
                              const currentId =
                                n.data.nodeType === "agent"
                                  ? String((n.data.config?.agentId as string) ?? "")
                                  : String((n.data.config?.toolId as string) ?? "");
                              const currentName = opts.find((o) => o.id === currentId)?.name;
                              return (
                                <span className={`block w-full truncate text-left ${currentName ? "" : "text-muted-foreground"}`}>
                                  {currentName ?? placeholder}
                                </span>
                              );
                            })()}
                          </SelectTrigger>
                          <SelectContent>
                            {(n.data.nodeType === "agent" ? agentOptions : toolOptions).map((opt) => (
                              <SelectItem key={opt.id} value={opt.id}>
                                {opt.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {n.data.nodeType === "systemInput" && (
                      <div className="mb-2">
                        <div className="mb-1 flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">输入内容</span>
                          <span className="text-[11px] text-muted-foreground">text</span>
                        </div>
                        <div
                          className="rounded-xl border border-input bg-background px-3 py-2 shadow-sm focus-within:ring-2 focus-within:ring-ring"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <textarea
                            value={String((n.data.config?.userInput as string) ?? "")}
                            onChange={(e) => {
                              const v = e.target.value;
                              setNodes((prev) =>
                                prev.map((x) => {
                                  if (x.id !== n.id) return x;
                                  return { ...x, data: { ...x.data, config: { ...(x.data.config ?? {}), userInput: v } } };
                                }),
                              );
                            }}
                            rows={2}
                            className="w-full resize-none bg-transparent text-sm leading-6 outline-none placeholder:text-muted-foreground"
                            placeholder="请输入工作流输入…"
                          />
                        </div>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      {n.data.inputs.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onFinishConnect(n.id, p.id, p.type);
                              }}
                              onMouseEnter={() => setHoverTarget({ nodeId: n.id, portId: p.id, type: p.type })}
                              onMouseLeave={() => setHoverTarget((t) => (t?.portId === p.id ? null : t))}
                              className="h-3.5 w-3.5 rounded-full border"
                              style={{
                                borderColor: portColor(p.type),
                                background: hoverTarget?.portId === p.id && connectFrom ? portColor(p.type) + "14" : "white",
                              }}
                              title={`${p.type}`}
                            />
                            <span className="text-xs text-muted-foreground">{p.label}</span>
                          </div>
                          <span className="text-[11px] text-muted-foreground">{p.type}</span>
                        </div>
                      ))}
                      {n.data.outputs.map((p) => (
                        <div key={p.id} className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{p.label}</span>
                            <button
                              onMouseDown={(e) => onStartConnect(n.id, p.id, p.type, e)}
                              className={`h-3.5 w-3.5 rounded-full border ${connectFrom?.fromPortId === p.id ? "ring-2 ring-ring ring-offset-2 ring-offset-white" : ""}`}
                              style={{ borderColor: portColor(p.type), background: portColor(p.type) + "1A" }}
                              title={`${p.type}`}
                            />
                          </div>
                          <span className="text-[11px] text-muted-foreground">{p.type}</span>
                        </div>
                      ))}
                      {n.data.inputs.length === 0 && n.data.outputs.length === 0 && (
                        <div className="py-2 text-xs text-muted-foreground">无端口</div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          <div className="absolute bottom-3 right-3 rounded-lg border border-border bg-white px-2.5 py-1 text-[11px] text-muted-foreground shadow-sm">
            {Math.round(zoom * 100)}%
          </div>
        </div>

        {selectedEdgeId && (() => {
          const edge = edges.find((e) => e.id === selectedEdgeId);
          if (!edge) return null;
          const from = getNode(edge.fromNodeId);
          const to = getNode(edge.toNodeId);
          const a = portAnchor(from, edge.fromPortId);
          const b = portAnchor(to, edge.toPortId);
          const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
          const client = {
            x: mid.x * zoom + pan.x,
            y: mid.y * zoom + pan.y,
          };
          const rect = containerRef.current?.getBoundingClientRect();
          if (!rect) return null;
          return (
            <div
              className="absolute z-[55]"
              style={{
                left: client.x + rect.left,
                top: client.y + rect.top,
                transform: "translate(-50%,-50%)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="inline-flex h-8 items-center gap-2 rounded-full border border-border bg-white px-3 text-xs font-medium text-foreground shadow-md transition-colors hover:bg-accent"
                onClick={() => {
                  setEdges((prev) => prev.filter((x) => x.id !== selectedEdgeId));
                  setSelectedEdgeId(null);
                }}
              >
                <Trash2 className="h-4 w-4 text-muted-foreground" />
                断开连接
              </button>
            </div>
          );
        })()}

        {quickAdd?.open && (
          <div
            className="fixed z-[60] w-[260px] overflow-hidden rounded-2xl border border-border bg-white shadow-lg"
            style={{ left: quickAdd.clientX + 10, top: quickAdd.clientY + 10 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-border px-3 py-2 text-sm font-semibold">接下来添加</div>
            <div className="p-2">
              {nodeCatalog
                .filter((n) => n.type !== "systemInput")
                .map((n) => (
                  <button
                    key={n.type}
                    onClick={() => {
                      const newId = addNode(n.type, { x: quickAdd.worldX, y: quickAdd.worldY });
                      const created = getNode(newId);
                      const firstInput = created.data.inputs[0];
                      if (firstInput && canConnect(quickAdd.from.fromType, firstInput.type)) {
                        setEdges((prev) => [
                          ...prev,
                          {
                            id: uid("edge"),
                            fromNodeId: quickAdd.from.fromNodeId,
                            fromPortId: quickAdd.from.fromPortId,
                            toNodeId: newId,
                            toPortId: firstInput.id,
                          },
                        ]);
                      }
                      setQuickAdd(null);
                    }}
                    className="flex w-full items-start gap-2 rounded-xl px-3 py-2 text-left transition-colors hover:bg-accent"
                  >
                    {(() => {
                      const Icon = catalogIcon(n.type);
                      return (
                        <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-900">
                          <Icon className="h-4 w-4" />
                        </div>
                      );
                    })()}
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{n.title}</div>
                      {n.subtitle && <div className="mt-0.5 text-xs text-muted-foreground">{n.subtitle}</div>}
                    </div>
                  </button>
                ))}
            </div>
            <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">提示：拖拽连线到空白处松手可快速添加节点</div>
          </div>
        )}

        {paletteOpen && (
          <div className="absolute left-1/2 top-16 z-50 w-[520px] -translate-x-1/2 overflow-hidden rounded-2xl border border-border bg-white shadow-lg">
            <div className="flex items-center gap-2 border-b border-border px-3 py-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <input
                value={paletteQuery}
                onChange={(e) => setPaletteQuery(e.target.value)}
                placeholder="搜索节点、标签、工作流"
                className="h-9 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
              <button
                onClick={() => setPaletteOpen(false)}
                className="rounded-lg px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
              >
                关闭
              </button>
            </div>
            <div className="grid gap-2 p-3 md:grid-cols-2">
              {catalogFiltered.map((n) => (
                <button
                  key={n.type}
                  onClick={() => addNode(n.type)}
                  className="flex items-start gap-3 rounded-xl border border-border bg-white px-3 py-3 text-left transition-colors hover:bg-accent"
                >
                  {(() => {
                    const Icon = catalogIcon(n.type);
                    return (
                      <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-900">
                        <Icon className="h-4 w-4" />
                      </div>
                    );
                  })()}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{n.title}</div>
                    {n.subtitle && <div className="mt-0.5 text-xs text-muted-foreground">{n.subtitle}</div>}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between border-t border-border px-3 py-2">
              <div className="text-xs text-muted-foreground">提示：点输出端口后，再点目标输入端口完成连线</div>
              <button className="inline-flex h-8 items-center gap-2 rounded-lg border border-input bg-background px-3 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-accent">
                <UploadCloud className="h-3.5 w-3.5" />
                导入
              </button>
            </div>
          </div>
        )}

        {/* Report preview */}
        {reportOpen && (
          <div className="fixed inset-0 z-[80] bg-black/35 backdrop-blur-sm" onClick={() => setReportOpen(false)}>
            <div
              className="absolute right-6 top-6 bottom-6 w-[min(980px,calc(100%-48px))] rounded-2xl border border-border bg-white shadow-2xl overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex h-12 items-center justify-between border-b border-border px-4">
                <div className="text-sm font-semibold">可视化报告</div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const blob = new Blob([reportHtml], { type: "text/html;charset=utf-8" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = "final_report.html";
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                    disabled={reportLoading || !reportHtml}
                    className="inline-flex h-9 items-center rounded-lg border border-input bg-background px-3 text-xs font-medium hover:bg-accent disabled:opacity-60"
                  >
                    导出 HTML
                  </button>
                  <button onClick={() => setReportOpen(false)} className="inline-flex h-9 items-center rounded-lg border border-input bg-background px-3 text-xs font-medium hover:bg-accent">
                    关闭
                  </button>
                </div>
              </div>
              <div className="min-h-0 flex-1 bg-muted">
                {reportLoading ? (
                  <div className="h-full w-full bg-[#0b1220] p-6">
                    <div className="mx-auto max-w-[980px]">
                      <div className="h-[120px] rounded-2xl bg-white/10 animate-pulse" />
                      <div className="mt-4 grid gap-4 md:grid-cols-2">
                        <div className="rounded-2xl bg-white/8 p-5">
                          <div className="h-4 w-24 rounded bg-white/15 animate-pulse" />
                          <div className="mt-4 space-y-3">
                            <div className="h-20 rounded-xl bg-white/10 animate-pulse" />
                            <div className="h-20 rounded-xl bg-white/10 animate-pulse" />
                            <div className="h-20 rounded-xl bg-white/10 animate-pulse" />
                          </div>
                        </div>
                        <div className="rounded-2xl bg-white/8 p-5">
                          <div className="h-4 w-28 rounded bg-white/15 animate-pulse" />
                          <div className="mt-4 h-16 rounded-xl bg-white/10 animate-pulse" />
                          <div className="mt-4 h-24 rounded-xl bg-white/10 animate-pulse" />
                          <div className="mt-4 h-24 rounded-xl bg-white/10 animate-pulse" />
                        </div>
                      </div>
                      <div className="mt-4 h-10 w-40 rounded-xl bg-white/10 animate-pulse" />
                    </div>
                  </div>
                ) : (
                  <iframe title="report" className="h-full w-full" srcDoc={reportHtml} />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Run workflow button (fixed bottom center) */}
        {!reportOpen && (
          <div className="pointer-events-none fixed left-1/2 z-[75] -translate-x-1/2" style={{ bottom: 40 }}>
            <button
              onClick={handleRun}
              disabled={running}
              className="pointer-events-auto inline-flex h-10 items-center gap-2 rounded-xl bg-primary px-5 text-sm font-medium text-primary-foreground shadow-[0_18px_60px_rgba(15,23,42,0.18)] transition-colors hover:opacity-90 disabled:opacity-70"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              运行工作流
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

